/**
 * AuthContext — Firebase Authentication for QuotedsArrow.
 *
 * Provides:
 *   currentUser   — the signed-in Firebase user (or null)
 *   isLoading     — true while the auth state is being resolved on mount
 *   signInWithGoogle()
 *   signInWithApple()
 *   signInWithEmail(email, password)
 *   signUp(email, password)
 *   signOut()
 *
 * On sign-in the Firebase ID token is registered with the API client's
 * setAuthTokenGetter so every subsequent fetch automatically carries
 * Authorization: Bearer <token>.
 *
 * On first-ever sign-in, any stored referral/campaign params from localStorage
 * are sent to POST /api/profile/attribution to credit the referrer.
 */
import {
  createContext,
  useContext,
  useEffect,
  useState,
  useCallback,
  type ReactNode,
} from "react";
import {
  onAuthStateChanged,
  signInWithPopup,
  signInWithEmailAndPassword,
  createUserWithEmailAndPassword,
  signOut as firebaseSignOut,
  GoogleAuthProvider,
  OAuthProvider,
  type User,
} from "firebase/auth";
import { auth, firebaseConfigured } from "@/lib/firebase";
import { setAuthTokenGetter } from "@workspace/api-client-react";
import { queryClient } from "@/lib/queryClient";

interface AuthContextValue {
  currentUser: User | null;
  isLoading: boolean;
  firebaseConfigured: boolean;
  signInWithGoogle: () => Promise<void>;
  signInWithApple: () => Promise<void>;
  signInWithEmail: (email: string, password: string) => Promise<void>;
  signUp: (email: string, password: string) => Promise<void>;
  sendPasswordReset: (email: string) => Promise<void>;
  signOut: () => Promise<void>;
}

const AuthContext = createContext<AuthContextValue | null>(null);

const googleProvider = new GoogleAuthProvider();
const appleProvider = new OAuthProvider("apple.com");

function sendAttributionIfNew(user: User): void {
  const createdMs = user.metadata.creationTime
    ? new Date(user.metadata.creationTime).getTime()
    : 0;
  const lastSignInMs = user.metadata.lastSignInTime
    ? new Date(user.metadata.lastSignInTime).getTime()
    : 0;
  const isNewUser = Math.abs(lastSignInMs - createdMs) < 30_000;
  if (!isNewUser) return;

  const storedRef = localStorage.getItem("referral_ref");
  const storedCampaign = localStorage.getItem("referral_campaign");
  if (!storedRef && !storedCampaign) return;

  user
    .getIdToken()
    .then((token) =>
      fetch("/api/profile/attribution", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({
          ...(storedRef ? { referralCode: storedRef } : {}),
          ...(storedCampaign ? { campaign: storedCampaign } : {}),
        }),
      }),
    )
    .then((res) => {
      if (res.ok) {
        localStorage.removeItem("referral_ref");
        localStorage.removeItem("referral_campaign");
      }
    })
    .catch(() => {
      // attribution is best-effort — silent failure
    });
}

export function AuthProvider({ children }: { children: ReactNode }) {
  const [currentUser, setCurrentUser] = useState<User | null>(null);
  const [isLoading, setIsLoading] = useState(firebaseConfigured);

  useEffect(() => {
    if (!firebaseConfigured) return;

    let prevUid: string | null = null;

    const unsubscribe = onAuthStateChanged(auth, (user) => {
      // Clear user-scoped query cache whenever the signed-in account changes
      // (sign-out, or switching between accounts) to prevent stale data leaking.
      if (prevUid !== null && prevUid !== (user?.uid ?? null)) {
        queryClient.clear();
      }
      prevUid = user?.uid ?? null;

      setCurrentUser(user);
      setIsLoading(false);

      if (user) {
        setAuthTokenGetter(() => user.getIdToken());
        sendAttributionIfNew(user);
      } else {
        setAuthTokenGetter(null);
      }
    });

    return unsubscribe;
  }, []);

  const signInWithGoogle = useCallback(async () => {
    await signInWithPopup(auth, googleProvider);
  }, []);

  const signInWithApple = useCallback(async () => {
    await signInWithPopup(auth, appleProvider);
  }, []);

  const signInWithEmail = useCallback(async (email: string, password: string) => {
    await signInWithEmailAndPassword(auth, email, password);
  }, []);

  const signUp = useCallback(async (email: string, password: string) => {
    await createUserWithEmailAndPassword(auth, email, password);
  }, []);

  const sendPasswordReset = useCallback(async (email: string) => {
    const res = await fetch(`${import.meta.env.BASE_URL}api/auth/send-password-reset`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ email }),
    });
    if (!res.ok) {
      const data = await res.json().catch(() => ({}));
      throw new Error((data as { error?: string }).error ?? "Failed to send reset email.");
    }
  }, []);

  const signOut = useCallback(async () => {
    setAuthTokenGetter(null);
    await firebaseSignOut(auth);
  }, []);

  return (
    <AuthContext.Provider
      value={{
        currentUser,
        isLoading,
        firebaseConfigured,
        signInWithGoogle,
        signInWithApple,
        signInWithEmail,
        signUp,
        sendPasswordReset,
        signOut,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth(): AuthContextValue {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error("useAuth must be used within <AuthProvider>");
  return ctx;
}
